import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getCookie, clearAllCookies } from '../helpers/cookie';
import { getRefreshToken } from '../helpers/auth';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { jwtDecode } from 'jwt-decode';
import { useDispatch } from 'react-redux';

const SessionManager = ({ children }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const rootRef = useRef(null);
  const refreshTimeoutRef = useRef(null);

  const dispatch = useDispatch();

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  };

  // Check token expiration and refresh if needed
  //  const checkTokenExpiration = useCallback(async () => {
  //     const accessToken = getCookie('accessToken');
  //     if (!accessToken) return;

  //     try {
  //       const decoded = jwtDecode(accessToken);
  //       if (!decoded.exp) return;

  //       const expirationTime = decoded.exp * 1000;
  //       const currentTime = Date.now();
  //       const timeRemaining = expirationTime - currentTime;
  //       const fiveMinutesInMs = 5 * 60 * 1000;

  //       console.log('Current time:', new Date(currentTime).toLocaleString());
  //       console.log('Token expires at:', new Date(expirationTime).toLocaleString());
  //       console.log('Time remaining (ms):', timeRemaining);

  //       if (timeRemaining <= fiveMinutesInMs && timeRemaining > 0) {
  //         // console.log('Token expiring soon, refreshing...');
  //         dispatch(getRefreshToken());
  //       }
  //     } catch (error) {
  //       console.error('Error checking token expiration:', error);
  //     }
  //   }, [dispatch]);
  const accessToken = getCookie('accessToken');

  const checkTokenExpiration = useCallback(async () => {
    const accessToken = getCookie('accessToken');
    if (!accessToken) return;

    try {
      const decoded = jwtDecode(accessToken);
      if (!decoded.exp) return;

      const expirationTime = decoded.exp * 1000;
      const currentTime = Date.now();
      const timeRemaining = expirationTime - currentTime;
      const fiveMinutesInMs = 5 * 60 * 1000;


      if (timeRemaining <= fiveMinutesInMs && timeRemaining > 0) {
        const lastRefresh = localStorage.getItem('lastTokenRefresh');
        if (lastRefresh && Date.now() - parseInt(lastRefresh, 10) < 60000) {
          return; // Another tab is currently refreshing the token
        }
        localStorage.setItem('lastTokenRefresh', Date.now().toString());

        const response = await getRefreshToken()

        if (!response) {
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error('Error checking token expiration:', error);
      // dispatch({ type: 'REFRESH_TOKEN_FAILURE', payload: error.message });
    }
  }, [dispatch]);



  // Track user activity and check token on activity
  useEffect(() => {
    const rootElement = rootRef.current || document.getElementById('root');
    if (!rootElement) return;

    const updateActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      localStorage.setItem('lastActivity', now.toString());
      checkTokenExpiration();
    };

    const events = [
      // 'mousemove',
      // 'mousedown',
      // 'mouseenter',
      'click',
      'dblclick',
      'contextmenu',
      'keydown',
      // 'scroll',
      // 'touchstart',
      // 'mouseover',
      // 'click',
      // 'input',
      // 'wheel',
      // 'resize',
    ];
    events.forEach((event) => rootElement.addEventListener(event, updateActivity));

    // Handle hover state
    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);
    rootElement.addEventListener('mouseenter', handleMouseEnter);
    rootElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      events.forEach((event) => rootElement.removeEventListener(event, updateActivity));
      rootElement.removeEventListener('mouseenter', handleMouseEnter);
      rootElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [checkTokenExpiration]);

  useEffect(() => {

    checkTokenExpiration();


    const interval = setInterval(checkTokenExpiration, 60 * 1000);

    return () => clearInterval(interval);
  }, [checkTokenExpiration]);


  useEffect(() => {
    const SESSION_TIMEOUT = 30 * 60 * 1000;
    const WARNING_TIME = 5 * 60 * 1000;

    // Update last activity on mount to consider opening a tab as activity
    const nowOnMount = Date.now();
    lastActivityRef.current = nowOnMount;
    localStorage.setItem('lastActivity', nowOnMount.toString());

    const checkSessionTimeout = () => {
      const now = Date.now();

      const storedLastActivity = localStorage.getItem('lastActivity');
      const lastActivity = storedLastActivity
        ? Math.max(parseInt(storedLastActivity, 10), lastActivityRef.current)
        : lastActivityRef.current;

      lastActivityRef.current = lastActivity;

      const timeSinceLastActivity = now - lastActivity;
      const remainingTime = SESSION_TIMEOUT - timeSinceLastActivity;

      if (remainingTime <= WARNING_TIME && remainingTime > 0) {
        setTimeLeft(Math.ceil(remainingTime / 1000));
        setShowWarning(true);
      } else if (remainingTime <= 0) {
        localStorage.removeItem('lastActivity');
        clearAllCookies();
        window.location.href = '/login';
      } else {
        setShowWarning(false);
      }
    };

    const sessionCheckInterval = setInterval(checkSessionTimeout, 1000);
    return () => clearInterval(sessionCheckInterval);
  }, []);

  return (
    <div ref={rootRef}>
      {children}
      {accessToken && <Snackbar
        open={showWarning}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setShowWarning(false)}
        autoHideDuration={null}
      >
        <Alert
          severity="info"
          sx={{ width: '100%' }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          Your session will expire in {timeLeft} seconds. {isHovering && 'onClick your mouse to stay logged in!'}
        </Alert>
      </Snackbar>}
    </div>
  );
};

export default SessionManager;